import { MapContainer, Marker, Popup, TileLayer } from 'react-leaflet';
import L, { Marker as MarkerProp, Map as LeafletMap } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-rotate';
import { Skeleton } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { RobotMapIcon, WorkerMapIcon } from './map_icon';
import { WorkerResponse } from '../../../model/workers_response';

import icon from '../../../../assets/images/debug_icons.png';
import { useMainStore } from '../../pages/main/main_store';

/**
 * エリアマップのプロパティ
 */
export type AreaMapProps = {
  isLoading: boolean;
  mapCode: string;
  floorCode: string;
  floorId: number;
  center: [number, number];
  angle: number;
  zoom: number;
  maxZoom: number;
  minZoom: number;
  robots?: WorkerResponse;
};

/**
 * エリアマップ
 * @param isLoading
 * @param mapCode
 * @param floorId
 * @param floorCode
 * @param center
 * @param zoom
 * @param maxZoom
 * @param minZoom
 * @param robots
 * @constructor
 */
export const AreaMap: React.FC<AreaMapProps> = ({ isLoading, mapCode, floorId, floorCode, center, angle, zoom, maxZoom, minZoom, robots }) => {
  const [iconPos, setIconPos] = useState<[number, number]>(center);
  const mapRef = useRef<LeafletMap>(null);
  const markerRef = useRef<MarkerProp>(null);
  const selected = useMainStore((state) => state.selectedWorkerId);
  const handler = useMemo(() => {
    return {
      dragend() {
        if (markerRef.current) {
          const pos = markerRef.current.getLatLng();
          setIconPos([parseFloat(pos.lat.toFixed(6)), parseFloat(pos.lng.toFixed(6))]);
        }
      },
    };
  }, []);

  if (isLoading) {
    return (
        <div style={{ width: '100%', height: '100%' }}>
            <Skeleton.Button active={true} size={'large'} block={true} style={{ height: '100%' }}/>
        </div>
    );
  }

  useEffect(() => {
    const search = () => {
      if (selected?.type === 'robot') {
        const robo = robots?.robots.filter((r) => r.floorId === floorId).find((robot) => robot.robotId === selected?.id);

        if (robo && (robo.position.lat !== null && robo.position.lng !== null)) {
          mapRef.current?.panTo(robo.position);
          return true;
        }
        return false;
      }
      if (selected?.type === 'worker') {
        const worker = robots?.workers.filter((w) => w.floorId === floorId).find((worker) => worker.deviceId === selected?.id);
        if (worker) {
          mapRef.current?.panTo(worker.position);
          return true;
        }
        return false;
      }
      return true;
    };
    setTimeout(() => { search(); }, 100);
  }, [robots, floorId, selected]);

  return (
      <div>
          <MapContainer
              key={mapCode + floorCode + angle}
              ref={mapRef}
              style={{
                height: '100%',
                width: '100%',
              }}
              attributionControl={false}
              center={center}
              zoom={zoom}
              rotate={true}
              touchRotate={false}
              bearing={angle}
              // @ts-ignore
              rotateControl={false}
          >
              <TileLayer
                  url={`/api/resources/maps/${mapCode}/${floorCode}/{z}/{y}/{x}.png`}
                  maxZoom={maxZoom}
                  minZoom={minZoom}
              />
              {
                  // デバッグ用のアイコン
                  false && <Marker autoPanOnFocus={false} autoPan={false} ref={markerRef} icon={L.icon({ iconUrl: icon, iconSize: [30, 30] })} eventHandlers={handler} draggable={true} position={iconPos}>
                      <Popup>
                          <div>
                              <div>{`${iconPos[0]}, ${iconPos[1]}`}</div>
                              地図上の位置を表示するデバッグ用のアイコンです。
                          </div>
                      </Popup>
                  </Marker>
              }
            {robots?.robots.filter((robot) => robot.floorId === floorId && (robot.position.lat !== null && robot.position.lng !== null)).map((robot) => <RobotMapIcon key={robot.robotId} robot={robot}/>)}
            {robots?.workers.filter((worker) => worker.floorId === floorId).map((worker) => <WorkerMapIcon key={worker.deviceId} worker={worker}/>)}
          </MapContainer>
      </div>
  );
};
